import { Link } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";

/**
 * Cinq cercles de partenaires — Aoria affirme son ambition d'écosystème.
 */
const BLOCS = [
    {
        num: "01",
        title: "Enseignement",
        body: "Écoles, collèges, lycées, universités, grandes écoles, organismes de formation. Penser la place des outils numériques dans les apprentissages et l'évaluation, pas par défaut, mais par choix.",
    },
    {
        num: "02",
        title: "Recherche",
        body: "Sciences cognitives, sciences de l'éducation, ergonomie, sociologie du numérique, informatique : Aoria s'appuie sur le travail scientifique pour éviter les approximations et nourrir un débat solide.",
    },
    {
        num: "03",
        title: "Associations et terrain",
        body: "Associations engagées sur l'accessibilité, structures d'accompagnement, médiation numérique, acteurs de l'inclusion : faire remonter les pratiques réelles, partager les ressources et amplifier les voix peu entendues.",
    },
    {
        num: "04",
        title: "Entreprises du numérique",
        body: "Éditeurs, agences, concepteurs de services et de produits numériques : intégrer l'accessibilité cognitive en amont, pas en correctif. Aoria documente, conseille et plaide pour des pratiques d'accessibilité par défaut.",
    },
    {
        num: "05",
        title: "Acteurs de l'intelligence artificielle",
        body: "Laboratoires, start-ups, plateformes : confronter les promesses aux usages réels, défendre des modèles d'accès équitables, et faire émerger des outils qui réduisent les obstacles cognitifs au lieu de les déplacer.",
    },
];

export default function Partenaires() {
    return (
        <PageLayout testid="partenaires-page">
            <Seo
                title="Partenaires · Coopérations pour l'accessibilité cognitive · Aoria"
                description="Aoria construit un écosystème autour de l'accessibilité cognitive à l'ère de l'intelligence artificielle. Coopérations avec l'enseignement, la recherche, les associations, les entreprises du numérique et les acteurs de l'IA."
                path="/partenaires"
            />
            <PageHeader
                testid="partenaires"
                eyebrow="PARTENAIRES"
                title="Construire un écosystème,"
                italic="avec celles et ceux qui agissent"
                lead="L'accessibilité cognitive à l'ère numérique n'est ni un sujet technique, ni un sujet d'experts isolés. C'est un enjeu transverse qui mobilise l'enseignement, la recherche, le monde associatif, les entreprises du numérique et les acteurs de l'intelligence artificielle."
                backdrop="network"
            />

            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
                <p className="text-[#1B263B] text-base md:text-lg leading-relaxed max-w-3xl mb-16 md:mb-20">
                    Aoria recherche activement des coopérations avec ces cinq
                    cercles. Notre ambition est de bâtir une plateforme de
                    référence, capable de dialoguer avec les institutions
                    publiques, les laboratoires, les associations et
                    l'industrie, sans inféodation à aucune d'entre elles.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {BLOCS.map((b, i) => (
                        <article
                            key={b.title}
                            data-testid={`partenaires-bloc-${i}`}
                            className="card-aoria"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <span className="editorial-num text-sm">
                                    {b.num}
                                </span>
                                <span className="eyebrow text-[#778DA9]">
                                    Cercle
                                </span>
                            </div>
                            <h2 className="h3 text-xl md:text-2xl text-[#0D1B2A] leading-snug">
                                {b.title}
                            </h2>
                            <p className="mt-5 text-[#5C6B7D] text-sm md:text-base leading-relaxed flex-1">
                                {b.body}
                            </p>
                        </article>
                    ))}
                </div>

                {/* Statut actuel */}
                <section
                    data-testid="partenaires-statut"
                    className="mt-24 md:mt-32 border-t border-[#E0E1DD] pt-16 md:pt-20"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                        <div className="lg:col-span-3">
                            <span className="editorial-num text-lg">06</span>
                            <div className="hairline mt-3 pb-3" />
                            <p className="eyebrow mt-4">État actuel</p>
                        </div>
                        <div className="lg:col-span-9 max-w-3xl">
                            <h2 className="h2 text-3xl md:text-4xl text-[#0D1B2A] leading-snug">
                                Les premiers partenariats{" "}
                                <span className="italic text-[#415A77]">
                                    sont en cours de conventionnement
                                </span>
                                .
                            </h2>
                            <p className="mt-8 text-[#1B263B] text-base md:text-lg leading-relaxed">
                                Conformément à notre principe, ne rien annoncer
                                que nous ne pouvons tenir, les partenariats ne
                                seront affichés ici qu'une fois formalisés. Si
                                vous représentez une institution, une équipe de
                                recherche, une entreprise ou une association
                                partageant cette exigence, écrivez-nous : nous
                                examinons chaque proposition avec attention.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="partenaires-cta-contact"
                                    className="btn-primary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                                >
                                    Proposer un partenariat
                                </Link>
                                <Link
                                    to="/enjeu"
                                    data-testid="partenaires-cta-enjeux"
                                    className="btn-secondary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                                >
                                    Lire les enjeux
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </section>
        </PageLayout>
    );
}
